import { motion } from "framer-motion";
import { Car, Home, Droplets, Sparkles, Sofa, Sun } from "lucide-react";

const SERVICES = [
  {
    id: "car",
    name: "Car Cleaning",
    desc: "Interior vacuum, exterior wash, polish & dashboard detailing for all vehicle sizes.",
    img: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    icon: Car,
  },
  {
    id: "house",
    name: "House Cleaning",
    desc: "Deep cleaning for apartments, villas and independent homes — top to bottom.",
    img: "https://images.pexels.com/photos/6195114/pexels-photo-6195114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    icon: Home,
  },
  {
    id: "tank",
    name: "Tank Cleaning",
    desc: "Hygienic water tank cleaning & sanitisation — plastic, concrete & overhead tanks.",
    img: "https://images.unsplash.com/photo-1759795075769-0ef64c53b6e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    icon: Droplets,
  },
  {
    id: "sofa",
    name: "Sofa Cleaning",
    desc: "Deep sofa & upholstery shampoo. Stains, dust mites and odours — all gone.",
    img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    icon: Sofa,
  },
  {
    id: "solar",
    name: "Solar Panel Cleaning",
    desc: "Boost panel efficiency with safe, thorough rooftop solar cleaning.",
    img: "https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    icon: Sun,
  },
  {
    id: "all",
    name: "All Cleanings",
    desc: "Office, carpet, kitchen — whatever you need cleaned, we handle it.",
    img: "https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    icon: Sparkles,
  },
];

export default function Services() {
  const setServiceAndScroll = (serviceId) => () => {
    const event = new CustomEvent("shine:select-service", { detail: serviceId });
    window.dispatchEvent(event);
    document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-sky-600">What we clean</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Services tailored to <span className="text-sky-600">spotless results</span>.
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-base">
            From a quick car polish to a full home deep clean — we arrive equipped, trained and on time.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                onClick={setServiceAndScroll(s.id)}
                className="card-soft group text-left rounded-2xl overflow-hidden"
                data-testid={`service-card-${s.id}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                  <span className="absolute top-4 left-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-sky-600 shadow-lg">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-slate-900 group-hover:text-sky-700 transition">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sky-600 group-hover:gap-2 transition-all">
                    Book this service →
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
